import React, { useEffect, useState, useCallback } from "react";
import { X, Download, ExternalLink, Loader2, FileWarning } from "lucide-react";
import api from "../services/api";

export const openPdfInViewer = (url, title = "Document") => {
  window.dispatchEvent(new CustomEvent("ujms:open-pdf", { detail: { url, title } }));
};

export function PdfViewer() {
  const [doc, setDoc] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [isImage, setIsImage] = useState(false);
  const [filename, setFilename] = useState("document.pdf");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const close = useCallback(() => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setDoc(null); setBlobUrl(null); setError(null);
  }, [blobUrl]);

  useEffect(() => {
    const onOpen = async (e) => {
      const { url, title, body } = e.detail;
      setDoc({ url, title }); setBlobUrl(null); setError(null); setLoading(true);
      try {
        const res = body ? await api.post(url, body, { responseType: "blob" }) : await api.get(url, { responseType: "blob" });
        const cd = res.headers?.["content-disposition"] || "";
        const m = cd.match(/filename="?([^";]+)"?/);
        const ct = res.headers?.["content-type"] || res.data?.type || "application/pdf";
        const img = ct.startsWith("image/");
        setIsImage(img);
        setFilename(m ? m[1] : img ? "image" : "document.pdf");
        setBlobUrl(URL.createObjectURL(new Blob([res.data], { type: ct })));
      } catch {
        setError("Could not load this document. You may not have permission to view it.");
      } finally { setLoading(false); }
    };
    window.addEventListener("ujms:open-pdf", onOpen);
    return () => window.removeEventListener("ujms:open-pdf", onOpen);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    if (doc) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doc, close]);

  if (!doc) return null;

  const download = () => {
    const a = document.createElement("a");
    a.href = blobUrl; a.download = filename; a.click();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/80 flex flex-col" data-testid="pdf-viewer-overlay">
      <div className="h-12 flex items-center justify-between px-4 bg-slate-900 text-white shrink-0">
        <div className="text-sm font-medium truncate pr-4" data-testid="pdf-viewer-title">{doc.title}</div>
        <div className="flex items-center gap-1.5">
          {blobUrl && (
            <>
              <button onClick={download} data-testid="pdf-viewer-download-btn"
                className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-sm bg-slate-700 hover:bg-slate-600">
                <Download className="w-3.5 h-3.5" /> Download
              </button>
              <a href={blobUrl} target="_blank" rel="noopener noreferrer" data-testid="pdf-viewer-newtab-btn"
                className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-sm bg-slate-700 hover:bg-slate-600">
                <ExternalLink className="w-3.5 h-3.5" /> New tab
              </a>
            </>
          )}
          <button onClick={close} data-testid="pdf-viewer-close-btn"
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-sm bg-red-800 hover:bg-red-700 ml-2">
            <X className="w-3.5 h-3.5" /> Close
          </button>
        </div>
      </div>
      <div className="flex-1 bg-slate-700">
        {loading && (
          <div className="h-full flex items-center justify-center text-slate-300">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading document…
          </div>
        )}
        {error && (
          <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-2" data-testid="pdf-viewer-error">
            <FileWarning className="w-8 h-8 text-amber-400" />
            <div className="text-sm">{error}</div>
          </div>
        )}
        {blobUrl && isImage && (
          <div className="w-full h-full flex items-center justify-center overflow-auto p-6">
            <img src={blobUrl} alt={doc.title} className="max-w-full max-h-full object-contain shadow-2xl rounded-sm" data-testid="pdf-viewer-image" />
          </div>
        )}
        {blobUrl && !isImage && (
          <iframe src={blobUrl} title={doc.title} className="w-full h-full border-0" data-testid="pdf-viewer-frame" />
        )}
      </div>
    </div>
  );
}
